// Standalone control-room console (ADR 0016 decision 21): FireApp on its own page (console.html), served from FER's
// own hostname with the offline PMTiles basemap, so the control room works with the Hub shell and gateway down.
import { StrictMode, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import "../../../../../design/tokens.css";
import { FireApp } from "../FireApp";
import type { Principal } from "../types";
import { loadConsoleConfig, type ConsoleConfig } from "./config";
import { completeSignIn, principalFromAccessToken, refresh, startSignIn, type OidcSettings, type Tokens } from "./oidc";

function redirectUri(): string {
  const u = new URL(globalThis.location.href);
  u.search = "";
  u.hash = "";
  return u.href;
}

function Console({ config }: { config: ConsoleConfig }) {
  const settings: OidcSettings | null = useMemo(() => (config.oidc ? { ...config.oidc, redirectUri: redirectUri() } : null), [config.oidc]);
  const [tokens, setTokens] = useState<Tokens | null>(null);
  const tokensRef = useRef<Tokens | null>(null);
  tokensRef.current = tokens;
  const [principal, setPrincipal] = useState<Principal | null>(null);

  useEffect(() => {
    if (!settings) return;
    void completeSignIn(settings, globalThis.location.href).then((t) => {
      // Drop the code from the address bar whatever happened.
      globalThis.history.replaceState(null, "", redirectUri());
      if (t) {
        setTokens(t);
        setPrincipal(principalFromAccessToken(t.accessToken));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on load
  }, []);

  // Refresh a minute before expiry (review FW9). A failed refresh (identity down) is retried with backoff (5 s,
  // doubling, at most a minute apart) while the refresh token lasts. The current token is used until it expires (FER
  // validates it with cached keys); at expiry the principal is cleared, so the console offers the normal sign-in and
  // (when /local-auth/status allows it) the FER local sign-in. Every refreshed token updates the principal.
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!settings || !tokens?.refreshToken) return;
    const delay = attempt === 0 ? Math.max(5_000, tokens.expiresAt - Date.now() - 60_000) : Math.min(60_000, 5_000 * 2 ** (attempt - 1));
    let live = true;
    const t = setTimeout(() => {
      void refresh(settings, tokens.refreshToken!).then((n) => {
        if (!live) return;
        if (n) {
          setAttempt(0);
          setTokens(n);
          setPrincipal(principalFromAccessToken(n.accessToken));
        } else setAttempt((a) => a + 1);
      });
    }, delay);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [settings, tokens, attempt]);

  useEffect(() => {
    if (!tokens) return;
    const left = tokens.expiresAt - Date.now();
    const t = setTimeout(() => setPrincipal(null), Math.max(0, Math.min(left, 2 ** 31 - 1)));
    return () => clearTimeout(t);
  }, [tokens]);

  return (
    <FireApp
      apiBaseUrl={config.apiBaseUrl}
      getAccessToken={() => (tokensRef.current && tokensRef.current.expiresAt > Date.now() ? tokensRef.current.accessToken : "")}
      principal={principal}
      onSignIn={settings ? () => void startSignIn(settings) : undefined}
      basemap={config.basemap}
    />
  );
}

function Boot() {
  const [config, setConfig] = useState<ConsoleConfig | string | null>(null);
  useEffect(() => {
    void loadConsoleConfig().then(setConfig);
  }, []);
  if (config === null) return <p className="fer-app">Loading the console…</p>;
  if (typeof config === "string")
    return (
      <p className="fer-app" role="alert">
        The console is not configured: {config}
      </p>
    );
  return <Console config={config} />;
}

// Always the light theme (CLAUDE.md "Brand"): no dark mode by default.
document.documentElement.dataset.theme = "light";
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Boot />
  </StrictMode>,
);
